import { ReactNode } from "react";
import { useLanguage } from "@/i18n/LanguageContext";

/**
 * Shared, token-based diagram primitives for the Platform and Integrations
 * pages. All diagrams are inline SVG or CSS/flex layouts (sharp on Retina),
 * stack vertically on mobile and never overflow horizontally.
 */

export function DiagramFrame({
  label,
  children,
  caption,
}: {
  label: string;
  children: ReactNode;
  caption?: string;
}) {
  return (
    <figure
      role="group"
      aria-label={label}
      className="rounded-xl border border-border bg-card p-4 md:p-6 overflow-hidden"
    >
      {children}
      {caption && (
        <figcaption className="mt-4 text-xs text-muted-foreground border-t border-border pt-3">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

function Node({
  title,
  items,
  tone = "default",
}: {
  title: string;
  items?: string[];
  tone?: "default" | "primary" | "muted";
}) {
  const { t } = useLanguage();
  const toneClass =
    tone === "primary"
      ? "border-primary/50 bg-primary/10"
      : tone === "muted"
      ? "border-border bg-[hsl(var(--section-alt))]"
      : "border-border bg-background";
  return (
    <div className={`rounded-lg border ${toneClass} px-3 py-2.5 w-full`}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-foreground">
        {t(title)}
      </div>
      {items && (
        <ul className="mt-1 space-y-0.5">
          {items.map((i) => (
            <li key={i} className="text-[11px] leading-snug text-muted-foreground">
              {t(i)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Arrow({ text, dir = "down" }: { text?: string; dir?: "down" | "up" | "both" }) {
  const { t } = useLanguage();
  const glyph = dir === "up" ? "▲" : dir === "both" ? "▲▼" : "▼";
  return (
    <div className="flex items-center justify-center gap-2 py-1.5" aria-hidden="true">
      <span className="text-primary text-[10px] leading-none">{glyph}</span>
      {text && (
        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
          {t(text)}
        </span>
      )}
    </div>
  );
}

/** Closed operational loop: ERP → BEEDIGIT → shop floor → events → analytics → back to ERP. */
export function PlatformLoopDiagram() {
  const { t } = useLanguage();
  return (
    <DiagramFrame
      label={t("BEEDIGIT closed operational loop: plans from ERP and APS enter the BEEDIGIT execution layer, operators, machines and sensors execute the work, production, quality, material, maintenance and energy events are contextualised, dashboards and analytics expose the results, and verified confirmations return to ERP and BI.")}
      caption={t("Closed loop: plans flow down from business systems, verified facts flow back up.")}
    >
      <div className="space-y-0">
        <Node
          title="Business systems — ERP / APS"
          items={["Production plans, master data, routings, job orders"]}
          tone="muted"
        />
        <Arrow text="Plan, orders, master data" />
        <Node
          title="BEEDIGIT execution layer"
          items={["Dispatch · confirmations · context by order, machine, shift, batch"]}
          tone="primary"
        />
        <Arrow text="Work instructions" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Node title="Operators" />
          <Node title="Machines & PLCs" />
          <Node title="Sensors & meters" />
          <Node title="Scanners & printers" />
        </div>
        <Arrow text="Real production events" dir="up" />
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Node title="Production" />
          <Node title="Quality" />
          <Node title="Material" />
          <Node title="Maintenance" />
          <Node title="Energy" />
        </div>
        <Arrow text="Contextualised facts" dir="up" />
        <Node
          title="Dashboards & analytics"
          items={["OEE, downtime, plan vs actual, traceability, energy"]}
          tone="primary"
        />
        <Arrow text="Confirmed results back to ERP / BI" dir="up" />
        <Node
          title="ERP · BI · CMMS · QMS"
          items={["Output, scrap, consumption, order progress, verified KPIs"]}
          tone="muted"
        />
      </div>
    </DiagramFrame>
  );
}

/** Relationship map of the shared manufacturing data model. */
export function DataModelDiagram() {
  const { t } = useLanguage();
  const ring = [
    "Work centers & equipment",
    "Products & routings",
    "Job orders & plans",
    "Operators & shifts",
    "Materials & batches",
    "Output & scrap",
    "Downtime & reason codes",
    "Quality checks & NCs",
    "Maintenance events",
    "Energy consumption",
  ];
  return (
    <DiagramFrame
      label={t("Shared manufacturing data model: work centers, products and routings, job orders, operators and shifts, materials and batches, output and scrap, downtime and reason codes, quality checks, maintenance events and energy consumption all reference the same production event.")}
      caption={t("Every entity references the same production event, so one downtime minute can be read by order, machine, shift, product and batch.")}
    >
      <div className="grid md:grid-cols-[1fr_auto_1fr] gap-3 items-center">
        <div className="space-y-2">
          {ring.slice(0, 5).map((r) => (
            <div
              key={r}
              className="rounded-md border border-border bg-background px-3 py-2 text-xs text-foreground/85"
            >
              {t(r)}
            </div>
          ))}
        </div>
        <div className="flex md:flex-col items-center justify-center gap-2 py-2">
          <div className="hidden md:block h-px w-8 bg-border" aria-hidden="true" />
          <div className="rounded-lg border border-primary/50 bg-primary/10 px-4 py-3 text-center">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
              {t("Shared key")}
            </div>
            <div className="text-sm font-semibold text-foreground">{t("Production event")}</div>
            <div className="text-[11px] text-muted-foreground">
              {t("order · machine · shift · product · batch · time")}
            </div>
          </div>
          <div className="hidden md:block h-px w-8 bg-border" aria-hidden="true" />
        </div>
        <div className="space-y-2">
          {ring.slice(5).map((r) => (
            <div
              key={r}
              className="rounded-md border border-border bg-background px-3 py-2 text-xs text-foreground/85"
            >
              {t(r)}
            </div>
          ))}
        </div>
      </div>
    </DiagramFrame>
  );
}

/** Four connected platform layers with what moves between them. */
export function LayersDiagram({
  layers,
}: {
  layers: { name: string; what: string; moves: string }[];
}) {
  const { t } = useLanguage();
  return (
    <div className="space-y-0">
      {layers.map((l, i) => (
        <div key={l.name}>
          <div className="rounded-lg border border-border bg-card p-5">
            <div className="flex items-baseline gap-3 mb-2">
              <span className="text-[11px] font-mono text-primary">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="font-semibold uppercase tracking-wider text-sm">{t(l.name)}</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-3">{t(l.what)}</p>
            <p className="text-xs text-foreground/80 border-t border-border pt-3">
              <span className="font-semibold">{t("Moves to the next layer:")}</span> {t(l.moves)}
            </p>
          </div>
          {i < layers.length - 1 && <Arrow dir="both" />}
        </div>
      ))}
    </div>
  );
}

/** Horizontal (desktop) / vertical (mobile) numbered process. */
export function ProcessTrack({ steps }: { steps: { n: number; title: string; body: string }[] }) {
  const { t } = useLanguage();
  return (
    <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
      {steps.map((s) => (
        <div
          key={s.n}
          className="relative rounded-lg border border-border bg-card p-4 flex md:block gap-3"
        >
          <div className="h-7 w-7 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold md:mb-2">
            {s.n}
          </div>
          <div>
            <div className="text-sm font-semibold mb-1 leading-snug">{t(s.title)}</div>
            <p className="text-xs text-muted-foreground leading-snug">{t(s.body)}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Expansion path, no rebuild. */
export function ScalePath({ stages }: { stages: { title: string; body: string }[] }) {
  const { t } = useLanguage();
  return (
    <div className="rounded-xl border border-border bg-card p-4 md:p-6">
      <div className="grid gap-2 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
        {stages.map((s, i) => (
          <div key={s.title} className="relative">
            <div className="rounded-lg border border-border bg-background p-3 h-full">
              <div
                className="h-1 rounded-full bg-primary mb-2"
                style={{ width: `${((i + 1) / stages.length) * 100}%` }}
                aria-hidden="true"
              />
              <div className="text-xs font-semibold mb-1">{t(s.title)}</div>
              <p className="text-[11px] text-muted-foreground leading-snug">{t(s.body)}</p>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs text-muted-foreground border-t border-border pt-3">
        {t("Same data model, same hardware, same interfaces at every stage — expansion adds scope, not a new system.")}
      </p>
    </div>
  );
}

/** Integrations hero: systems around BEEDIGIT with correct arrow directions. */
export function IntegrationMapDiagram() {
  const { t } = useLanguage();
  return (
    <DiagramFrame
      label={t("Integration map: ERP, APS and planning systems exchange data bi-directionally with BEEDIGIT; operators, machines and industrial hardware send data into BEEDIGIT and receive work instructions; QMS, CMMS, BI and data platforms mostly receive data from BEEDIGIT, with equipment and inspection master data optionally flowing back in.")}
      caption={t("Solid two-way exchanges are agreed per project; analytics destinations are typically read-only.")}
    >
      <div className="space-y-0">
        <Node
          title="ERP · APS · planning"
          items={["Orders, master data, BOM, routings, calendars"]}
          tone="muted"
        />
        <Arrow text="Bi-directional: plan in, confirmations out" dir="both" />
        <Node
          title="BEEDIGIT"
          items={["Execution, context, validation, event history"]}
          tone="primary"
        />
        <div className="grid md:grid-cols-2 gap-3 mt-1">
          <div>
            <Arrow text="Bi-directional: instructions down, facts up" dir="both" />
            <Node
              title="Operators · machines · hardware"
              items={["PLCs, sensors, meters, gateways, terminals, scanners, printers"]}
            />
          </div>
          <div>
            <Arrow text="Mostly outbound: events and KPIs" dir="down" />
            <Node
              title="QMS · CMMS · BI · data platforms"
              items={["Downtime, quality, maintenance, energy, OEE dimensions"]}
            />
          </div>
        </div>
      </div>
    </DiagramFrame>
  );
}

export type FlowRow = {
  object: string;
  direction: string;
  source: string;
  destination: string;
  purpose: string;
};

/** Responsive data-flow table → stacked cards on mobile. */
export function FlowTable({ rows }: { rows: FlowRow[] }) {
  const { t } = useLanguage();
  return (
    <>
      <div className="hidden md:block overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <caption className="sr-only">
            {t("Business objects exchanged between BEEDIGIT and connected systems, with direction, source, destination and operational purpose.")}
          </caption>
          <thead className="bg-[hsl(var(--section-alt))] text-foreground/80">
            <tr>
              <th scope="col" className="text-left p-3 font-medium">{t("Business object")}</th>
              <th scope="col" className="text-left p-3 font-medium">{t("Direction")}</th>
              <th scope="col" className="text-left p-3 font-medium">{t("Source")}</th>
              <th scope="col" className="text-left p-3 font-medium">{t("Destination")}</th>
              <th scope="col" className="text-left p-3 font-medium">{t("Operational purpose")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.object} className="border-t border-border align-top">
                <th scope="row" className="p-3 text-left font-semibold text-foreground/90">
                  {t(r.object)}
                </th>
                <td className="p-3 font-mono text-xs text-primary whitespace-nowrap">
                  {t(r.direction)}
                </td>
                <td className="p-3 text-muted-foreground">{t(r.source)}</td>
                <td className="p-3 text-muted-foreground">{t(r.destination)}</td>
                <td className="p-3 text-muted-foreground">{t(r.purpose)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="md:hidden space-y-3">
        {rows.map((r) => (
          <div key={r.object} className="rounded-lg border border-border bg-card p-4">
            <div className="font-semibold mb-1">{t(r.object)}</div>
            <div className="font-mono text-xs text-primary mb-2">{t(r.direction)}</div>
            <p className="text-sm text-muted-foreground">
              <span className="text-foreground/70">{t("From:")}</span> {t(r.source)}
            </p>
            <p className="text-sm text-muted-foreground">
              <span className="text-foreground/70">{t("To:")}</span> {t(r.destination)}
            </p>
            <p className="text-sm text-muted-foreground mt-2">{t(r.purpose)}</p>
          </div>
        ))}
      </div>
    </>
  );
}